import React, { useEffect, useMemo, useRef, useState } from "react";
import Navbar2 from "../Components/Navbar2";
import "../Components/css/EdiConverter.css";
import "../voices/voices-public-pages.css";

const SUPPORTED_EXTENSIONS = [".edi", ".x12", ".835", ".837", ".dat", ".remit", ".txt"];
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const SAMPLE_835 = [
  "ISA*00*          *00*          *ZZ*ANTRAZAL       *ZZ*PAYER          *260601*1200*^*00501*000000905*0*T*:~",
  "GS*HP*ANTRAZAL*PAYER*20260601*1200*1*X*005010X221A1~",
  "ST*835*0001~",
  "BPR*I*1250*C*ACH*CCP*01*123456789*DA*987654321*1512345678**01*987654321*DA*123456789*20260601~",
  "TRN*1*9988776655*1512345678~",
  "N1*PR*Example Health Plan~",
  "CLP*CLAIM-1001*1*1500*1000*50*12*ICN1001*11*1~",
  "NM1*QC*1*DOE*JANE****MI*MEM1001~",
  "SVC*HC:99213*500*400~",
  "DTM*472*20260512~",
  "CLP*CLAIM-1002*2*350*250*25*12*ICN1002*11*1~",
  "NM1*QC*1*SMITH*JOHN****MI*MEM1002~",
  "SVC*HC:93000*350*250~",
  "DTM*472*20260513~",
  "SE*13*0001~",
  "GE*1*1~",
  "IEA*1*000000905~",
].join("");

const SAMPLE_837 = [
  "ISA*00*          *00*          *ZZ*ANTRAZAL       *ZZ*RECEIVER       *260601*1215*^*00501*000000906*0*T*:~",
  "GS*HC*ANTRAZAL*RECEIVER*20260601*1215*2*X*005010X222A1~",
  "ST*837*0002*005010X222A1~",
  "BHT*0019*00*BATCH-5001*20260601*1215*CH~",
  "NM1*85*2*ANTRAZAL CLINIC*****XX*1234567893~",
  "NM1*IL*1*PATEL*RAVI****MI*SUB5001~",
  "CLM*CLM-2001*775***11:B:1*Y*A*Y*Y~",
  "HI*ABK:J189~",
  "LX*1~",
  "SV1*HC:99214*775*UN*1***1~",
  "NM1*82*1*MEHTA*ANIKA****XX*9876543210~",
  "SE*10*0002~",
  "GE*1*2~",
  "IEA*1*000000906~",
].join("");

const SEGMENT_NAMES = {
  ISA: "Interchange control header",
  GS: "Functional group header",
  ST: "Transaction set header",
  BHT: "Beginning of hierarchical transaction",
  BPR: "Financial information",
  TRN: "Trace number",
  N1: "Name",
  NM1: "Individual or organization name",
  CLP: "Claim payment information",
  CLM: "Claim information",
  SVC: "Service payment information",
  SV1: "Professional service",
  DTM: "Date or time reference",
  HI: "Health care diagnosis code",
  LX: "Assigned number",
  SE: "Transaction set trailer",
  GE: "Functional group trailer",
  IEA: "Interchange control trailer",
};

const defaultAdvancedOptions = {
  smartMapping: true,
  rawSegments: true,
  keyFields: true,
  allFields: false,
  combineByType: true,
  oneOutputPerFile: false,
  maskPrivateFields: false,
};

const advancedOptionItems = [
  { key: "maskPrivateFields", label: "Mask private fields" },
];

const initialState = {
  sourceFiles: [],
  results: [],
  issues: [],
};

const SUMMARY_COLUMNS = [
  "file",
  "detected",
  "transactions",
  "segments",
  "rowsFound",
  "issues",
  "controlNumber",
  "groupControlNumber",
];

const LINE_DATA_COLUMNS = [
  "file",
  "type",
  "lineType",
  "claimId",
  "statusCode",
  "chargeAmount",
  "paymentAmount",
  "patientResponsibility",
  "payerControlNumber",
  "payerName",
  "traceNumber",
  "paymentDate",
  "patientName",
  "memberId",
  "claimAmount",
  "facilityCode",
  "subscriberName",
  "billingProvider",
  "renderingProvider",
  "batchReference",
];

const RAW_COLUMNS = [
  "file",
  "index",
  "segment",
  "meaning",
  "elementCount",
  "elements",
  "raw",
];

const ISSUE_COLUMNS = [
  "file",
  "level",
  "problem",
  "message",
];

function escapeXml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function protectSpreadsheetValue(value) {
  const text = String(value ?? "");
  return /^\s*[=+\-@]/.test(text) ? `'${text}` : text;
}

function humanizeHeader(value) {
  const labels = {
    file: "File",
    detected: "Detected Type",
    transactions: "Transactions",
    segments: "Segments",
    rowsFound: "Rows Found",
    issues: "Issues",
    controlNumber: "Control Number",
    groupControlNumber: "Group Control Number",
    index: "Index",
    segment: "Segment",
    meaning: "Meaning",
    elementCount: "Element Count",
    elements: "Elements",
    raw: "Raw Segment",
    type: "Document Type",
    lineType: "Line Type",
    claimId: "Claim ID",
    statusCode: "Status Code",
    chargeAmount: "Charge Amount",
    paymentAmount: "Payment Amount",
    patientResponsibility: "Patient Responsibility",
    payerControlNumber: "Payer Control Number",
    payerName: "Payer Name",
    traceNumber: "Trace Number",
    paymentDate: "Payment Date",
    patientName: "Patient Name",
    memberId: "Member ID",
    claimAmount: "Claim Amount",
    facilityCode: "Facility Code",
    subscriberName: "Subscriber Name",
    billingProvider: "Billing Provider",
    renderingProvider: "Rendering Provider",
    batchReference: "Batch Reference",
    level: "Level",
    problem: "Problem",
    message: "Message",
  };

  if (labels[value]) {
    return labels[value];
  }

  const elementMatch = String(value).match(/^element(\d+)$/i);
  if (elementMatch) {
    return `Element ${elementMatch[1]}`;
  }

  return String(value)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function sanitizeFilename(value) {
  return String(value || "edi-file")
    .replace(/\.[^.]+$/, "")
    .replace(/[^a-z0-9_-]+/gi, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 80) || "edi-file";
}

function getExtension(fileName) {
  const match = String(fileName || "").toLowerCase().match(/\.[^.]+$/);
  return match ? match[0] : "";
}

function normalizeText(text) {
  return String(text || "").replace(/^\uFEFF/, "").trim();
}

function maskValue(value) {
  const text = String(value ?? "");
  if (!text) {
    return text;
  }
  if (/^\d+(\.\d+)?$/.test(text)) {
    return text;
  }
  if (text.length <= 3) {
    return "***";
  }
  return `${text.slice(0, 1)}${"*".repeat(Math.min(text.length - 2, 8))}${text.slice(-1)}`;
}

function maybeMask(value, enabled) {
  if (!enabled) {
    return value ?? "";
  }
  return maskValue(value);
}

function splitX12Segments(text) {
  const clean = normalizeText(text);
  if (!clean) {
    return { rows: [], elementSeparator: "*", segmentTerminator: "~" };
  }

  const elementSeparator = clean.startsWith("ISA") && clean.length > 3 ? clean.charAt(3) : "*";
  let segmentTerminator = clean.includes("~") ? "~" : "\n";
  if (clean.startsWith("ISA") && clean.length > 105) {
    const isaTerminator = clean.charAt(105);
    if (isaTerminator && !/[A-Za-z0-9\s]/.test(isaTerminator)) {
      segmentTerminator = isaTerminator;
    }
  }
  const parts =
    segmentTerminator === "\n"
      ? clean.split(/\r?\n/)
      : clean.split(segmentTerminator);

  const rows = parts
    .map((segment) => segment.trim())
    .filter(Boolean)
    .map((segment, index) => {
      const elements = segment.split(elementSeparator);
      const tag = (elements[0] || "").trim();
      return {
        index: index + 1,
        tag,
        name: SEGMENT_NAMES[tag] || "EDI segment",
        elements,
        raw: segment,
      };
    });

  return { rows, elementSeparator, segmentTerminator };
}

function findElement(rows, tag, position) {
  const row = rows.find((item) => item.tag === tag);
  return row?.elements?.[position] || "";
}

function nameFromNm1(elements) {
  const lastOrOrg = elements[3] || "";
  const first = elements[4] || "";
  const middle = elements[5] || "";
  return [first, middle, lastOrOrg].filter(Boolean).join(" ").trim() || lastOrOrg;
}

function buildWarnings(rows, detectedType, extension) {
  const warnings = [];
  if (extension && !SUPPORTED_EXTENSIONS.includes(extension)) {
    warnings.push({
      level: "warning",
      title: "This file extension is not on the supported list.",
      message: `We still checked the content because EDI files are often saved as ${extension}.`,
    });
  }
  if (!rows.length) {
    warnings.push({
      level: "error",
      title: "We could not read this file.",
      message: "The file is empty or does not contain readable EDI text.",
    });
    return warnings;
  }
  if (detectedType === "unknown-x12") {
    warnings.push({
      level: "warning",
      title: "We found an X12 file, but not an 835 or 837.",
      message: "You can still review and download the raw EDI table.",
    });
  }
  if (detectedType === "unknown") {
    warnings.push({
      level: "error",
      title: "We could not fully read this file.",
      message: "You can still download the raw text view, or try another file.",
    });
  }

  rows.forEach((row, index) => {
    if (row.tag !== "ST") {
      return;
    }
    const trailerIndex = rows.findIndex((candidate, candidateIndex) => candidateIndex > index && candidate.tag === "SE");
    if (trailerIndex === -1) {
      warnings.push({
        level: "warning",
        title: "A transaction is missing its trailer.",
        message: `The transaction starting at segment ${row.index} does not have an SE trailer segment.`,
      });
      return;
    }
    const expected = Number(rows[trailerIndex].elements[1]);
    const actual = trailerIndex - index + 1;
    if (expected && expected !== actual) {
      warnings.push({
        level: "warning",
        title: "A segment count does not match.",
        message: `Transaction ${row.elements[2] || row.index} says ${expected} segments, but we counted ${actual}.`,
      });
    }
  });

  return warnings;
}

function extract835Lines(rows, options) {
  const lines = [];
  let payerName = "";
  let traceNumber = "";
  let paymentDate = "";

  rows.forEach((row) => {
    const elements = row.elements;
    if (row.tag === "N1" && elements[1] === "PR") {
      payerName = elements[2] || payerName;
    }
    if (row.tag === "TRN") {
      traceNumber = elements[2] || traceNumber;
    }
    if (row.tag === "BPR") {
      paymentDate = elements[16] || paymentDate;
    }
    if (row.tag === "CLP") {
      lines.push({
        lineType: "Payment claim",
        claimId: maybeMask(elements[1], options.maskPrivateFields),
        statusCode: elements[2] || "",
        chargeAmount: elements[3] || "",
        paymentAmount: elements[4] || "",
        patientResponsibility: elements[5] || "",
        payerControlNumber: maybeMask(elements[7], options.maskPrivateFields),
        payerName: maybeMask(payerName, options.maskPrivateFields),
        traceNumber: maybeMask(traceNumber, options.maskPrivateFields),
        paymentDate,
      });
    }
    if (row.tag === "NM1" && elements[1] === "QC" && lines.length) {
      lines[lines.length - 1].patientName = maybeMask(nameFromNm1(elements), options.maskPrivateFields);
      lines[lines.length - 1].memberId = maybeMask(elements[9], options.maskPrivateFields);
    }
  });

  return lines;
}

function extract837Lines(rows, options) {
  const lines = [];
  let billingProvider = "";
  let subscriber = "";
  let batchReference = "";

  rows.forEach((row) => {
    const elements = row.elements;
    if (row.tag === "BHT") {
      batchReference = elements[3] || batchReference;
    }
    if (row.tag === "NM1" && elements[1] === "85") {
      billingProvider = nameFromNm1(elements) || billingProvider;
    }
    if (row.tag === "NM1" && elements[1] === "82") {
      if (lines.length) {
        lines[lines.length - 1].renderingProvider = maybeMask(nameFromNm1(elements), options.maskPrivateFields);
      }
    }
    if (row.tag === "NM1" && elements[1] === "IL") {
      subscriber = nameFromNm1(elements) || subscriber;
    }
    if (row.tag === "CLM") {
      lines.push({
        lineType: "Claim",
        claimId: maybeMask(elements[1], options.maskPrivateFields),
        claimAmount: elements[2] || "",
        facilityCode: elements[5] || "",
        subscriberName: maybeMask(subscriber, options.maskPrivateFields),
        billingProvider: maybeMask(billingProvider, options.maskPrivateFields),
        renderingProvider: "",
        batchReference: maybeMask(batchReference, options.maskPrivateFields),
      });
    }
  });

  return lines;
}

function detectDocumentType(rows) {
  const transactionCodes = rows.filter((row) => row.tag === "ST").map((row) => row.elements[1]);
  if (transactionCodes.includes("835") && transactionCodes.includes("837")) {
    return {
      code: "835/837",
      kind: "mixed-x12",
      title: "Mixed X12 835 and 837 file",
      shortTitle: "Mixed X12",
    };
  }
  if (transactionCodes.includes("835")) {
    return {
      code: "835",
      kind: "x12-835",
      title: "Payment / remittance file 835",
      shortTitle: "835 Payment",
    };
  }
  if (transactionCodes.includes("837")) {
    return {
      code: "837",
      kind: "x12-837",
      title: "Claim file 837",
      shortTitle: "837 Claim",
    };
  }
  if (rows.some((row) => ["ISA", "GS", "ST"].includes(row.tag))) {
    return {
      code: "X12",
      kind: "unknown-x12",
      title: "X12 raw file",
      shortTitle: "Raw X12",
    };
  }
  return {
    code: "Unknown",
    kind: "unknown",
    title: "Unknown EDI text",
    shortTitle: "Unknown",
  };
}

function parseEdiFile(item, options) {
  const extension = getExtension(item.name);
  const cleanText = normalizeText(item.text);
  const { rows, elementSeparator, segmentTerminator } = splitX12Segments(cleanText);
  const detected = detectDocumentType(rows);
  const warnings = buildWarnings(rows, detected.kind, extension);
  const lines =
    detected.kind === "x12-835"
      ? extract835Lines(rows, options)
      : detected.kind === "x12-837"
        ? extract837Lines(rows, options)
        : detected.kind === "mixed-x12"
          ? [...extract835Lines(rows, options), ...extract837Lines(rows, options)]
          : [];

  const rawRows = rows.map((row) => ({
    fileName: item.name,
    index: row.index,
    tag: row.tag,
    name: row.name,
    elementCount: Math.max(row.elements.length - 1, 0),
    elements: row.elements.slice(1).map((value) => maybeMask(value, options.maskPrivateFields)),
    raw: options.maskPrivateFields ? maskValue(row.raw) : row.raw,
  }));

  return {
    id: item.id,
    fileName: item.name,
    source: item.source,
    size: item.size || cleanText.length,
    detected,
    elementSeparator,
    segmentTerminator: segmentTerminator === "\n" ? "line break" : segmentTerminator,
    summary: {
      controlNumber: findElement(rows, "ISA", 13),
      groupControlNumber: findElement(rows, "GS", 6),
      transactionCount: rows.filter((row) => row.tag === "ST").length,
      segmentCount: rows.length,
      lineCount: lines.length,
      issueCount: warnings.length,
    },
    lines,
    rawRows,
    issues: warnings,
    originalText: options.maskPrivateFields ? "Masked in preview while privacy masking is on." : cleanText,
  };
}

function createRowsForExport(results, mode) {
  if (mode === "raw") {
    return results.flatMap((result) =>
      result.rawRows.map((row) => {
        const elementValues = row.elements.reduce((next, value, index) => {
          next[`element${index + 1}`] = value;
          return next;
        }, {});

        return {
          file: result.fileName,
          index: row.index,
          segment: row.tag,
          meaning: row.name,
          elementCount: row.elementCount,
          elements: row.elements.join(" | "),
          ...elementValues,
          raw: row.raw,
        };
      })
    );
  }

  if (mode === "summary") {
    return results.map((result) => ({
      file: result.fileName,
      detected: result.detected.title,
      transactions: result.summary.transactionCount,
      segments: result.summary.segmentCount,
      rowsFound: result.summary.lineCount,
      issues: result.summary.issueCount,
      controlNumber: result.summary.controlNumber,
      groupControlNumber: result.summary.groupControlNumber,
    }));
  }

  if (mode === "issues") {
    return results.flatMap((result) =>
      result.issues.map((issue) => ({
        file: result.fileName,
        level: issue.level,
        problem: issue.title,
        message: issue.message,
      }))
    );
  }

  return results.flatMap((result) =>
    result.lines.map((line) => ({
      file: result.fileName,
      type: result.detected.shortTitle,
      ...line,
    }))
  );
}

function getColumnsForMode(mode, rows = []) {
  const configuredColumns =
    mode === "raw"
      ? RAW_COLUMNS
      : mode === "summary"
        ? SUMMARY_COLUMNS
        : mode === "issues"
          ? ISSUE_COLUMNS
        : LINE_DATA_COLUMNS;

  if (mode === "raw") {
    const elementColumns = Array.from(rows.reduce((set, row) => {
      Object.keys(row || {}).forEach((key) => {
        if (/^element\d+$/i.test(key)) {
          set.add(key);
        }
      });
      return set;
    }, new Set())).sort((first, second) => Number(first.replace(/\D/g, "")) - Number(second.replace(/\D/g, "")));
    const otherColumns = Array.from(rows.reduce((set, row) => {
      Object.keys(row || {}).forEach((key) => {
        if (!configuredColumns.includes(key) && !/^element\d+$/i.test(key)) {
          set.add(key);
        }
      });
      return set;
    }, new Set()));
    return [
      ...configuredColumns.filter((column) => column !== "raw"),
      ...elementColumns,
      "raw",
      ...otherColumns,
    ];
  }

  const extraColumns = Array.from(rows.reduce((set, row) => {
    Object.keys(row || {}).forEach((key) => {
      if (!configuredColumns.includes(key)) {
        set.add(key);
      }
    });
    return set;
  }, new Set()));
  return [...configuredColumns, ...extraColumns];
}

function normalizeRowsForColumns(rows, columns) {
  return rows.map((row) =>
    columns.reduce((next, column) => {
      next[column] = row?.[column] ?? "";
      return next;
    }, {})
  );
}

function csvValue(value) {
  const guarded = protectSpreadsheetValue(value);
  return `"${guarded.replace(/"/g, '""')}"`;
}

function rowsToCsv(rows, mode = "lines") {
  const headers = getColumnsForMode(mode, rows);

  if (!headers.length) {
    return "";
  }

  return [
    headers.map((header) => csvValue(humanizeHeader(header))).join(","),
    ...rows.map((row) => headers.map((header) => csvValue(row[header])).join(",")),
  ].join("\n");
}

function columnName(index) {
  let name = "";
  let value = index + 1;
  while (value > 0) {
    const remainder = (value - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    value = Math.floor((value - remainder) / 26);
  }
  return name;
}

function buildExportTable(rows, mode = "lines") {
  const headers = getColumnsForMode(mode, rows);

  return {
    headers,
    displayHeaders: headers.map(humanizeHeader),
    bodyRows: normalizeRowsForColumns(rows, headers).map((row) => headers.map((header) => protectSpreadsheetValue(row[header]))),
  };
}

function createInlineStringCell(value, rowIndex, columnIndex, style = 0) {
  const cellRef = `${columnName(columnIndex)}${rowIndex}`;
  const styleAttr = style ? ` s="${style}"` : "";
  return `<c r="${cellRef}" t="inlineStr"${styleAttr}><is><t>${escapeXml(value)}</t></is></c>`;
}

function createIssueRowsForExport(results, stateIssues = []) {
  const sessionIssues = stateIssues.map((issue) => ({
    file: "Session",
    level: issue.level,
    problem: issue.title,
    message: issue.message,
  }));
  return [...sessionIssues, ...createRowsForExport(results, "issues")];
}

function buildExportDataset(results, stateIssues = []) {
  const summaryRows = createRowsForExport(results, "summary");
  const detailedRows = createRowsForExport(results, "lines");
  const rawRows = createRowsForExport(results, "raw");
  const issueRows = createIssueRowsForExport(results, stateIssues);

  return {
    files: results.map((result) => ({
      file: result.fileName,
      source: result.source,
      size: result.size,
      detected: result.detected.title,
      transactionCount: result.summary.transactionCount,
      segmentCount: result.summary.segmentCount,
      mappedRows: result.summary.lineCount,
      issueCount: result.summary.issueCount,
      elementSeparator: result.elementSeparator,
      segmentTerminator: result.segmentTerminator,
    })),
    summaryRows,
    detailedRows,
    rawRows,
    issueRows,
  };
}

function createWorksheetXml({ title, rows, mode }) {
  const { headers, displayHeaders, bodyRows } = buildExportTable(rows, mode);
  const exportRows = [[title], displayHeaders, ...bodyRows];
  const lastColumn = columnName(Math.max(headers.length - 1, 0));
  const sheetRows = exportRows
    .map((row, rowOffset) => {
      const rowNumber = rowOffset + 1;
      const style = rowNumber === 1 ? 1 : rowNumber === 2 ? 2 : 0;
      const cells = row.map((value, columnIndex) => createInlineStringCell(value, rowNumber, columnIndex, style)).join("");
      return `<row r="${rowNumber}">${cells}</row>`;
    })
    .join("");

  const columns = displayHeaders
    .map((header, index) => {
      const maxBodyWidth = Math.max(
        header.length,
        ...bodyRows.map((row) => String(row[index] ?? "").length)
      );
      const width = Math.min(Math.max(maxBodyWidth + 2, 12), 48);
      return `<col min="${index + 1}" max="${index + 1}" width="${width}" customWidth="1"/>`;
    })
    .join("");

  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">',
    "<sheetViews><sheetView workbookViewId=\"0\"><pane ySplit=\"2\" topLeftCell=\"A3\" activePane=\"bottomLeft\" state=\"frozen\"/></sheetView></sheetViews>",
    `<cols>${columns}</cols>`,
    `<sheetData>${sheetRows}</sheetData>`,
    headers.length > 1 ? `<mergeCells count="1"><mergeCell ref="A1:${lastColumn}1"/></mergeCells>` : "",
    "</worksheet>",
  ].join("");
}

function createJsonExport(results, stateIssues = []) {
  const dataset = buildExportDataset(results, stateIssues);
  return {
    exportedAt: new Date().toISOString(),
    files: dataset.files,
    summaryRows: normalizeRowsForColumns(dataset.summaryRows, getColumnsForMode("summary", dataset.summaryRows)),
    detailedRows: normalizeRowsForColumns(dataset.detailedRows, getColumnsForMode("lines", dataset.detailedRows)),
    rawRows: normalizeRowsForColumns(dataset.rawRows, getColumnsForMode("raw", dataset.rawRows)),
    issues: normalizeRowsForColumns(dataset.issueRows, getColumnsForMode("issues", dataset.issueRows)),
  };
}

function createWorkbookStylesXml() {
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">',
    '<fonts count="3"><font><sz val="11"/><name val="Arial"/></font><font><b/><sz val="14"/><color rgb="FFFFFFFF"/><name val="Arial"/></font><font><b/><sz val="11"/><color rgb="FF111827"/><name val="Arial"/></font></fonts>',
    '<fills count="3"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FF2858E8"/><bgColor indexed="64"/></patternFill></fill><fill><patternFill patternType="solid"><fgColor rgb="FFEFF4FF"/><bgColor indexed="64"/></patternFill></fill></fills>',
    '<borders count="2"><border/><border><left style="thin"><color rgb="FFD9E2EF"/></left><right style="thin"><color rgb="FFD9E2EF"/></right><top style="thin"><color rgb="FFD9E2EF"/></top><bottom style="thin"><color rgb="FFD9E2EF"/></bottom></border></borders>',
    '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>',
    '<cellXfs count="3"><xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0"/><xf numFmtId="0" fontId="1" fillId="1" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1"/><xf numFmtId="0" fontId="2" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1"/></cellXfs>',
    '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>',
    "</styleSheet>",
  ].join("");
}

function createXlsxContentTypesXml(sheetCount) {
  const sheetOverrides = Array.from({ length: sheetCount }, (_, index) =>
    `<Override PartName="/xl/worksheets/sheet${index + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`
  ).join("");
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">',
    '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>',
    '<Default Extension="xml" ContentType="application/xml"/>',
    '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>',
    sheetOverrides,
    '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>',
    "</Types>",
  ].join("");
}

function safeSheetName(value, index) {
  const cleaned = String(value || `Sheet ${index + 1}`)
    .replace(/[\][:*?/\\]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return (cleaned || `Sheet ${index + 1}`).slice(0, 31);
}

function rowsToXlsxBlob(results, stateIssues = []) {
  const dataset = buildExportDataset(results, stateIssues);
  const sheets = [
    { name: "Detailed Data", title: "Detailed EDI Data", rows: dataset.detailedRows, mode: "lines" },
    { name: "Raw EDI", title: "Raw EDI Segments", rows: dataset.rawRows, mode: "raw" },
    { name: "Issues", title: "EDI Issues", rows: dataset.issueRows, mode: "issues" },
    { name: "Summary", title: "EDI File Summary", rows: dataset.summaryRows, mode: "summary" },
  ];

  const workbook = [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">',
    "<sheets>",
    sheets.map((sheet, index) =>
      `<sheet name="${escapeXml(safeSheetName(sheet.name, index))}" sheetId="${index + 1}" r:id="rId${index + 1}"/>`
    ).join(""),
    "</sheets>",
    "</workbook>",
  ].join("");

  const workbookRelationships = [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">',
    sheets.map((sheet, index) =>
      `<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${index + 1}.xml"/>`
    ).join(""),
    `<Relationship Id="rId${sheets.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>`,
    "</Relationships>",
  ].join("");

  return createZipBlob([
    {
      name: "[Content_Types].xml",
      content: createXlsxContentTypesXml(sheets.length),
    },
    {
      name: "_rels/.rels",
      content: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>',
    },
    {
      name: "xl/workbook.xml",
      content: workbook,
    },
    {
      name: "xl/_rels/workbook.xml.rels",
      content: workbookRelationships,
    },
    ...sheets.map((sheet, index) => ({
      name: `xl/worksheets/sheet${index + 1}.xml`,
      content: createWorksheetXml(sheet),
    })),
    {
      name: "xl/styles.xml",
      content: createWorkbookStylesXml(),
    },
  ]);
}

function crc32(bytes) {
  const table = crc32.table || (crc32.table = Array.from({ length: 256 }, (_, index) => {
    let value = index;
    for (let bit = 0; bit < 8; bit += 1) {
      value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }
    return value >>> 0;
  }));

  let crc = 0xffffffff;
  bytes.forEach((byte) => {
    crc = table[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  });
  return (crc ^ 0xffffffff) >>> 0;
}

function push16(target, value) {
  target.push(value & 0xff, (value >>> 8) & 0xff);
}

function push32(target, value) {
  const next = value >>> 0;
  target.push(next & 0xff, (next >>> 8) & 0xff, (next >>> 16) & 0xff, (next >>> 24) & 0xff);
}

function createZipBlob(files) {
  const encoder = new TextEncoder();
  const localChunks = [];
  const centralChunks = [];
  let offset = 0;
  let centralSize = 0;
  const dosTime = 0;
  const dosDate = 33;

  files.forEach((file) => {
    const fileName = file.name.replace(/^\/+/, "");
    const nameBytes = encoder.encode(fileName);
    const dataBytes = encoder.encode(file.content);
    const crc = crc32(dataBytes);
    const local = [];

    push32(local, 0x04034b50);
    push16(local, 20);
    push16(local, 0);
    push16(local, 0);
    push16(local, dosTime);
    push16(local, dosDate);
    push32(local, crc);
    push32(local, dataBytes.length);
    push32(local, dataBytes.length);
    push16(local, nameBytes.length);
    push16(local, 0);

    localChunks.push(Uint8Array.from(local), nameBytes, dataBytes);

    const central = [];
    push32(central, 0x02014b50);
    push16(central, 20);
    push16(central, 20);
    push16(central, 0);
    push16(central, 0);
    push16(central, dosTime);
    push16(central, dosDate);
    push32(central, crc);
    push32(central, dataBytes.length);
    push32(central, dataBytes.length);
    push16(central, nameBytes.length);
    push16(central, 0);
    push16(central, 0);
    push16(central, 0);
    push16(central, 0);
    push32(central, 0);
    push32(central, offset);

    centralChunks.push(Uint8Array.from(central), nameBytes);
    const localSize = local.length + nameBytes.length + dataBytes.length;
    const centralHeaderSize = central.length + nameBytes.length;
    offset += localSize;
    centralSize += centralHeaderSize;
  });

  const end = [];
  push32(end, 0x06054b50);
  push16(end, 0);
  push16(end, 0);
  push16(end, files.length);
  push16(end, files.length);
  push32(end, centralSize);
  push32(end, offset);
  push16(end, 0);

  return new Blob([...localChunks, ...centralChunks, Uint8Array.from(end)], { type: "application/zip" });
}

function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1500);
}

function formatBytes(value) {
  if (!value) {
    return "0 KB";
  }
  if (value < 1024) {
    return `${value} bytes`;
  }
  if (value < 1024 * 1024) {
    return `${Math.round(value / 102.4) / 10} KB`;
  }
  return `${Math.round(value / 1024 / 102.4) / 10} MB`;
}

function uniqueId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export default function EdiConverter() {
  const [state, setState] = useState(initialState);
  const [pasteText, setPasteText] = useState("");
  const [stage, setStage] = useState(1);
  const [inputMode, setInputMode] = useState("file");
  const [activeFileId, setActiveFileId] = useState("");
  const [activeTab, setActiveTab] = useState("summary");
  const [advancedOptions, setAdvancedOptions] = useState(defaultAdvancedOptions);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const hasResults = state.results.length > 0;
  const activeResult = useMemo(
    () => state.results.find((result) => result.id === activeFileId) || state.results[0] || null,
    [activeFileId, state.results]
  );

  const aggregate = useMemo(() => {
    const segmentCount = state.results.reduce((sum, result) => sum + result.summary.segmentCount, 0);
    const rowCount = state.results.reduce((sum, result) => sum + result.summary.lineCount, 0);
    const issueCount = state.results.reduce((sum, result) => sum + result.summary.issueCount, 0) + state.issues.length;
    const detected = Array.from(new Set(state.results.map((result) => result.detected.shortTitle))).join(", ");
    return {
      fileCount: state.sourceFiles.length,
      segmentCount,
      rowCount,
      issueCount,
      detected: detected || "No file selected",
    };
  }, [state]);

  useEffect(() => {
    if (!state.sourceFiles.length) {
      return;
    }
    const parsed = state.sourceFiles.map((item) => parseEdiFile(item, advancedOptions));
    setState((previous) => ({ ...previous, results: parsed }));
  }, [advancedOptions, state.sourceFiles]);

  const parseAndSetFiles = (files) => {
    const nextResults = files.map((item) => parseEdiFile(item, advancedOptions));
    setState({
      sourceFiles: files,
      results: nextResults,
      issues: [],
    });
    setActiveFileId(nextResults[0]?.id || "");
    setActiveTab("summary");
    setStage(nextResults.length ? 2 : 1);
  };

  const readFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) {
      return;
    }

    const accepted = [];
    const issues = [];

    for (const file of files) {
      const extension = getExtension(file.name);
      if (file.size > MAX_FILE_SIZE_BYTES) {
        issues.push({
          level: "error",
          title: `${file.name} is too large.`,
          message: "Use a file under 10 MB for this browser converter.",
        });
        continue;
      }
      const text = await file.text();
      accepted.push({
        id: uniqueId("file"),
        name: file.name,
        source: "File",
        size: file.size,
        extension,
        text,
      });
    }

    const nextResults = accepted.map((item) => parseEdiFile(item, advancedOptions));
    setState({ sourceFiles: accepted, results: nextResults, issues });
    setActiveFileId(nextResults[0]?.id || "");
    setActiveTab("summary");
    setStage(nextResults.length ? 2 : 1);
  };

  const loadPaste = () => {
    if (!pasteText.trim()) {
      setState({
        sourceFiles: [],
        results: [],
        issues: [{
          level: "error",
          title: "Paste EDI text first.",
          message: "Paste an EDI file into the text box, then choose Check result.",
        }],
      });
      setStage(1);
      setActiveFileId("");
      setActiveTab("summary");
      return;
    }
    parseAndSetFiles([{
      id: uniqueId("paste"),
      name: "pasted-edi-text.x12",
      source: "Pasted text",
      size: pasteText.length,
      extension: ".x12",
      text: pasteText,
    }]);
  };

  const loadSample = (type) => {
    const text = type === "837" ? SAMPLE_837 : SAMPLE_835;
    setPasteText(text);
    parseAndSetFiles([{
      id: uniqueId(`sample-${type}`),
      name: `sample-${type}.x12`,
      source: "Sample",
      size: text.length,
      extension: ".x12",
      text,
    }]);
  };

  const clearAll = () => {
    setState(initialState);
    setPasteText("");
    setStage(1);
    setInputMode("file");
    setActiveFileId("");
    setActiveTab("summary");
    setAdvancedOptions(defaultAdvancedOptions);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    readFiles(event.dataTransfer.files);
  };

  const handleConvert = () => {
    if (!state.results.length) {
      setState((previous) => ({
        ...previous,
        issues: [{
          level: "error",
          title: "Choose or paste an EDI file first.",
          message: "The converter needs EDI text before it can create downloads.",
        }],
      }));
      setStage(1);
      return;
    }
    setStage(3);
    setActiveTab("summary");
  };

  const download = (format) => {
    if (!state.results.length) {
      return;
    }
    const baseName = state.results.length === 1 ? sanitizeFilename(state.results[0].fileName) : "antrazal-edi-conversion";
    const dataset = buildExportDataset(state.results, state.issues);

    if (format === "excel") {
      downloadBlob(
        rowsToXlsxBlob(state.results, state.issues),
        `${baseName}.xlsx`
      );
      return;
    }
    if (format === "csv") {
      downloadBlob(new Blob([rowsToCsv(dataset.detailedRows, "lines")], { type: "text/csv;charset=utf-8" }), `${baseName}.csv`);
      return;
    }
    if (format === "json") {
      const json = JSON.stringify(createJsonExport(state.results, state.issues), null, 2);
      downloadBlob(new Blob([json], { type: "application/json;charset=utf-8" }), `${baseName}.json`);
      return;
    }
    if (format === "zip") {
      const zipFiles = state.results.flatMap((result, index) => {
        const fileBase = `${String(index + 1).padStart(2, "0")}-${sanitizeFilename(result.fileName)}`;
        const resultDataset = buildExportDataset([result]);
        return [
          {
            name: `${fileBase}/detailed-data.csv`,
            content: rowsToCsv(resultDataset.detailedRows, "lines"),
          },
          {
            name: `${fileBase}/raw-edi.csv`,
            content: rowsToCsv(resultDataset.rawRows, "raw"),
          },
          {
            name: `${fileBase}/summary.csv`,
            content: rowsToCsv(resultDataset.summaryRows, "summary"),
          },
          {
            name: `${fileBase}/issues.csv`,
            content: rowsToCsv(resultDataset.issueRows, "issues"),
          },
          {
            name: `${fileBase}/result.json`,
            content: JSON.stringify(createJsonExport([result]), null, 2),
          },
        ];
      });
      if (state.issues.length) {
        zipFiles.push({
          name: "session-issues.csv",
          content: rowsToCsv(createIssueRowsForExport([], state.issues), "issues"),
        });
      }
      downloadBlob(createZipBlob(zipFiles), `${baseName}.zip`);
    }
  };

  return (
    <>
      <Navbar2 />

      <main className="edi-page vpp-page vpp-edi" data-voices-theme="dark">
        <header className="edi-hero vpp-intro" id="converter">
          <div className="container edi-hero__inner">
            <div className="edi-hero__copy">
              <p className="edi-eyebrow">Antrazal EDI Converter</p>
              <h1>Convert EDI to Excel in your browser</h1>
              <p className="edi-hero__lead">
                Upload or paste an EDI file. We convert it locally in your browser so you can download Excel, CSV, or JSON.
                No upload. No storage. No account.
              </p>
              <div className="edi-hero__actions">
                <button type="button" onClick={() => fileInputRef.current?.click()}>
                  Choose EDI File
                </button>
                <a href="#converter-tool" onClick={() => setInputMode("paste")}>Paste EDI Text</a>
                <span>Browser-only</span>
              </div>
            </div>
          </div>
        </header>

        <section className="edi-tool-section" id="converter-tool" data-voices-theme="dark">
          <div className="container">
            <p className="edi-sr-only" aria-live="polite">
              {hasResults ? `${aggregate.fileCount} file parsed. ${aggregate.rowCount} mapped rows found. ${aggregate.issueCount} issues.` : "EDI converter ready."}
            </p>
            <div className="edi-converter-card">
              <div className="edi-step-strip" aria-label="Conversion steps">
                {[
                  [1, "Choose/paste"],
                  [2, "Check tabs"],
                  [3, "Download"],
                ].map(([number, label]) => (
                  <div key={number} className={stage >= number ? "is-active" : ""}>
                    <strong>{label}</strong>
                  </div>
                ))}
              </div>

              <div className="edi-workspace">
                <div className="edi-input-panel">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    hidden
                    aria-label="Choose EDI files"
                    accept={SUPPORTED_EXTENSIONS.join(",")}
                    onChange={(event) => readFiles(event.target.files)}
                  />
                  <div className="edi-input-mode" aria-label="Input mode">
                    <button
                      type="button"
                      className={inputMode === "file" ? "is-active" : ""}
                      onClick={() => setInputMode("file")}
                      aria-pressed={inputMode === "file"}
                    >
                      Upload
                    </button>
                    <button
                      type="button"
                      className={inputMode === "paste" ? "is-active" : ""}
                      onClick={() => setInputMode("paste")}
                      aria-pressed={inputMode === "paste"}
                    >
                      Paste
                    </button>
                  </div>

                  {inputMode === "file" ? (
                    <div
                      className={`edi-dropzone ${isDragging ? "is-dragging" : ""}`}
                      onDragEnter={(event) => {
                        event.preventDefault();
                        setIsDragging(true);
                      }}
                      onDragOver={(event) => event.preventDefault()}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={handleDrop}
                    >
                      <h2>Choose file</h2>
                      <p>Drop an EDI file here or browse.</p>
                      <button type="button" onClick={() => fileInputRef.current?.click()}>
                        Choose File
                      </button>
                      <details className="edi-compact-details">
                        <summary>Supported files</summary>
                        <span>{SUPPORTED_EXTENSIONS.join(", ")}</span>
                      </details>
                    </div>
                  ) : (
                    <div className="edi-paste-panel" id="paste-edi">
                      <div>
                        <h3>Paste text</h3>
                        <div className="edi-sample-actions">
                          <button type="button" onClick={() => loadSample("835")}>Sample 835</button>
                          <button type="button" onClick={() => loadSample("837")}>Sample 837</button>
                        </div>
                      </div>
                      <textarea
                        value={pasteText}
                        onChange={(event) => setPasteText(event.target.value)}
                        placeholder="Paste EDI text here, for example ISA*00*..."
                        aria-label="Paste EDI text"
                      />
                      <button type="button" onClick={loadPaste}>
                        Use Pasted Text
                      </button>
                    </div>
                  )}

                  <details className="edi-privacy-details">
                    <summary>Privacy</summary>
                    <p>Your file is processed in this browser. Antrazal does not upload, store, or save it.</p>
                  </details>
                </div>

                <div className="edi-result-setup">
                  <h2>{hasResults ? aggregate.detected : "Check"}</h2>
                  <div className="edi-stat-grid">
                    <div>
                      <span>Files</span>
                      <strong>{aggregate.fileCount}</strong>
                    </div>
                    <div>
                      <span>Rows</span>
                      <strong>{aggregate.rowCount}</strong>
                    </div>
                    <div>
                      <span>Issues</span>
                      <strong>{aggregate.issueCount}</strong>
                    </div>
                  </div>

                  {state.sourceFiles.length === 1 && (
                    <div className="edi-current-file">
                      <strong>{state.sourceFiles[0].name}</strong>
                      <span>{formatBytes(state.sourceFiles[0].size)}</span>
                    </div>
                  )}

                  {state.sourceFiles.length > 1 && (
                    <div className="edi-file-list" aria-label="Selected files">
                      {state.sourceFiles.map((file) => (
                        <button
                          key={file.id}
                          type="button"
                          className={activeFileId === file.id ? "is-active" : ""}
                          onClick={() => setActiveFileId(file.id)}
                        >
                          <strong>{file.name}</strong>
                          <span>{formatBytes(file.size)}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  <details className="edi-advanced">
                    <summary>
                      <span>Options</span>
                    </summary>
                    <div className="edi-advanced__grid">
                      {advancedOptionItems.map(({ key, label, disabled }) => (
                        <label key={key} className={disabled ? "is-disabled" : ""}>
                          <input
                            type="checkbox"
                            checked={advancedOptions[key]}
                            disabled={disabled}
                            onChange={(event) =>
                              setAdvancedOptions((previous) => ({ ...previous, [key]: event.target.checked }))
                            }
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  </details>

                  <div className="edi-convert-actions">
                    <button type="button" className="edi-primary-action" onClick={handleConvert}>
                      Check
                    </button>
                    <button type="button" onClick={clearAll} disabled={!hasResults && !pasteText && !state.issues.length}>
                      Clear all
                    </button>
                  </div>
                </div>
              </div>

              {(state.issues.length > 0 || activeResult?.issues?.length > 0) && (
                <div className="edi-inline-errors" role="status">
                  {[...state.issues, ...(activeResult?.issues || [])].map((issue, index) => (
                    <div key={`${issue.title}-${index}`} className={`edi-issue edi-issue--${issue.level}`}>
                      <strong>{issue.title}</strong>
                      <span>{issue.message}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {hasResults && (
              <section className="edi-results-panel" aria-label="EDI conversion result">
                <div className="edi-results-panel__header">
                  <div>
                    <h2>{stage === 3 ? "Download" : "Results"}</h2>
                  </div>
                  <div className="edi-downloads">
                    <button type="button" className="edi-primary-action" onClick={() => download("excel")}>
                      Excel
                    </button>
                    <details className="edi-download-more">
                      <summary>More formats</summary>
                      <div>
                        <button type="button" onClick={() => download("csv")}>CSV</button>
                        <button type="button" onClick={() => download("json")}>JSON</button>
                        <button type="button" onClick={() => download("zip")}>ZIP</button>
                      </div>
                    </details>
                  </div>
                </div>

                <div className="edi-tabs" aria-label="Result views">
                  {["summary", "lines", "errors", "raw"].map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      className={activeTab === tab ? "is-active" : ""}
                      onClick={() => setActiveTab(tab)}
                      aria-pressed={activeTab === tab}
                    >
                      {tab === "raw" ? "Raw EDI" : tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
                </div>

                <ResultTab tab={activeTab} result={activeResult} stateIssues={state.issues} />
              </section>
            )}
          </div>
        </section>

        <section className="edi-details-section" data-voices-theme="dark">
          <div className="container edi-details-panel">
            <details>
              <summary>Supported files</summary>
              <p>
                X12 835, X12 837, and raw X12 segment viewing are supported. Excel exports include data rows and raw
                segment tabs.
              </p>
            </details>
            <details>
              <summary>Privacy</summary>
              <p>
                Files are processed in your browser. Nothing is uploaded or stored by this converter.
              </p>
            </details>
            <details>
              <summary>Flow</summary>
              <p>Choose/paste &gt; Check tabs &gt; Download.</p>
            </details>
          </div>
        </section>
      </main>
    </>
  );
}

function ResultTab({ tab, result, stateIssues }) {
  if (!result) {
    return null;
  }

  if (tab === "summary") {
    const rows = [
      ["File", result.fileName],
      ["Detected type", result.detected.title],
      ["Segments", result.summary.segmentCount],
      ["Mapped rows", result.summary.lineCount],
      ["Transaction sets", result.summary.transactionCount],
      ["Control number", result.summary.controlNumber || "Not found"],
      ["Group control number", result.summary.groupControlNumber || "Not found"],
      ["Element separator", result.elementSeparator],
      ["Segment terminator", result.segmentTerminator],
    ];
    return (
      <div className="edi-tab-panel">
        <div className="edi-summary-cards">
          <div><span>Detected</span><strong>{result.detected.shortTitle}</strong></div>
          <div><span>Rows found</span><strong>{result.summary.lineCount}</strong></div>
          <div><span>Issues</span><strong>{result.summary.issueCount}</strong></div>
        </div>
        <SimpleTable
          columns={["Field", "Value"]}
          rows={rows.map(([field, value]) => ({ Field: field, Value: value }))}
          emptyText="No summary available."
        />
      </div>
    );
  }

  if (tab === "lines") {
    const rows = createRowsForExport([result], "lines");
    return (
      <div className="edi-tab-panel">
        <SimpleTable
          columns={getColumnsForMode("lines", rows)}
          rows={rows}
          emptyText="No mapped claim or payment rows were found. Open Raw EDI to inspect the file."
        />
      </div>
    );
  }

  if (tab === "errors") {
    const rows = createIssueRowsForExport([result], stateIssues);
    return (
      <div className="edi-tab-panel">
        <SimpleTable columns={getColumnsForMode("issues", rows)} rows={rows} emptyText="No issues found." />
      </div>
    );
  }

  const rows = createRowsForExport([result], "raw");
  return (
    <div className="edi-tab-panel">
      <SimpleTable
        columns={getColumnsForMode("raw", rows)}
        rows={rows}
        emptyText="No raw EDI rows available."
      />
    </div>
  );
}

function SimpleTable({ columns, rows, emptyText }) {
  const tableColumns = columns;
  const normalizedRows = normalizeRowsForColumns(rows, tableColumns);

  if (!rows.length) {
    return <div className="edi-empty-state">{emptyText}</div>;
  }

  return (
    <div className="edi-table-wrap">
      <table className="edi-table">
        <thead>
          <tr>
            {tableColumns.map((column) => (
              <th key={column}>{humanizeHeader(column)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {normalizedRows.map((row, rowIndex) => (
            <tr key={`${rowIndex}-${row.index || row.claimId || row.Problem || "row"}`}>
              {tableColumns.map((column) => (
                <td key={column}>{String(row[column] ?? "")}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
